// File: frontend/src/pages/staff/StaffDetail.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/axios';

const StaffDetail = () => {
  const { id } = useParams();

  const [staff, setStaff] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Leave Modal State
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveType, setLeaveType] = useState('casual');
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [leaveError, setLeaveError] = useState(null);

  /**
   * Fetch staff profile details
   */
  const fetchStaffDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [staffRes, leavesRes] = await Promise.all([
        api.get(`/api/v1/staff/${id}`),
        api.get(`/api/v1/staff/leaves?staff_id=${id}`)
      ]);

      if (staffRes.data?.success) {
        setStaff(staffRes.data.data);
      } else {
        setError(staffRes.data?.message || 'Staff profile not found.');
      }

      if (leavesRes.data?.success) {
        setLeaves(leavesRes.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching staff profile:', err);
      setError(err.response?.data?.message || err.message || 'Error loading staff details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStaffDetails();
  }, [fetchStaffDetails]);

  /**
   * Approve or Reject a staff leave request
   */
  const handleUpdateLeaveStatus = async (leaveId, newStatus) => {
    try {
      const response = await api.put(`/api/v1/staff/leaves/${leaveId}`, {
        status: newStatus
      });
      if (response.data?.success) {
        fetchStaffDetails();
      } else {
        alert(response.data?.message || 'Failed to update leave status');
      }
    } catch (err) {
      console.error('Error updating leave status:', err);
      alert(err.response?.data?.message || err.message || 'Failed to update leave status');
    }
  };

  /**
   * Submit new leave application for staff member
   */
  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try {
      setSubmittingLeave(true);
      setLeaveError(null);

      const payload = {
        staff_id: id,
        leave_type: leaveType,
        from_date: fromDate,
        to_date: toDate,
        reason,
        status: 'pending'
      };

      const response = await api.post('/api/v1/staff/leaves', payload);

      if (response.data?.success) {
        setShowLeaveModal(false);
        setReason('');
        fetchStaffDetails();
      } else {
        setLeaveError(response.data?.message || 'Failed to submit leave application.');
      }
    } catch (err) {
      console.error('Error applying for leave:', err);
      setLeaveError(err.response?.data?.message || err.message || 'Failed to submit leave.');
    } finally {
      setSubmittingLeave(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500 font-medium">Loading staff profile...</p>
      </div>
    );
  }

  if (error || !staff) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 flex flex-col items-center justify-center">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 text-center max-w-md w-full shadow-sm">
          <p className="text-red-600 font-bold mb-4">{error || 'Staff member not found.'}</p>
          <Link to="/staff" className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-semibold">
            Return to Staff Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">{staff.name}</h1>
              <p className="text-xs text-slate-500">{staff.designation} • ID: {staff.staff_id}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowLeaveModal(true)}
              className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold px-4 py-2 rounded-xl text-xs transition"
            >
              + Apply Leave
            </button>
            <Link
              to={`/staff/payroll/${staff.id}/slip`}
              className="bg-primary hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded-xl text-xs shadow-sm transition"
            >
              Generate Salary Slip
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Profile Card Header */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-700 border border-indigo-200 flex items-center justify-center font-bold text-2xl">
              {staff.name.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{staff.name}</h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Staff Code: <span className="font-mono font-bold text-primary">{staff.staff_id}</span> • Department: <span className="font-semibold text-slate-800">{staff.department}</span>
              </p>
              <p className="text-xs text-slate-400 mt-1">Joined: {new Date(staff.join_date).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${
              staff.status === 'active' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-slate-100 text-slate-700'
            }`}>
              {staff.status}
            </span>
          </div>
        </div>

        {/* 4 Details Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card 1: Personal Details */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-3 text-xs">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Personal Details</h3>
            <div className="flex justify-between">
              <span className="text-slate-500">Father/Spouse:</span>
              <span className="font-semibold text-slate-800">{staff.father_name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">CNIC / ID:</span>
              <span className="font-mono font-semibold text-slate-800">{staff.cnic || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Gender:</span>
              <span className="font-semibold capitalize text-slate-800">{staff.gender}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Blood Group:</span>
              <span className="font-bold text-red-600">{staff.blood_group}</span>
            </div>
          </div>

          {/* Card 2: Contact & Address */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-3 text-xs">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Contact Details</h3>
            <div className="flex justify-between">
              <span className="text-slate-500">Phone:</span>
              <span className="font-mono font-semibold text-slate-800">{staff.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Email:</span>
              <span className="font-semibold text-slate-800">{staff.email || '-'}</span>
            </div>
            <div className="pt-1">
              <span className="text-slate-500 block mb-1">Residential Address:</span>
              <span className="font-medium text-slate-800">{staff.address || 'Not specified'}</span>
            </div>
          </div>

          {/* Card 3: Professional & Salary */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-3 text-xs">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Salary & Qualifications</h3>
            <div className="flex justify-between">
              <span className="text-slate-500">Basic Monthly Salary:</span>
              <span className="font-mono font-extrabold text-emerald-700 text-sm">Rs. {parseFloat(staff.salary || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Qualification:</span>
              <span className="font-semibold text-slate-800">{staff.qualification || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Experience:</span>
              <span className="font-semibold text-slate-800">{staff.experience_years} Years</span>
            </div>
          </div>

        </div>

        {/* Leave History Table Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-base">Leave Applications & History</h3>
            <button
              onClick={() => setShowLeaveModal(true)}
              className="text-xs font-bold text-primary hover:underline"
            >
              + Submit Leave
            </button>
          </div>

          {leaves.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-xs">
              No leave records found for this staff member.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                    <th className="py-3 px-4">Leave Type</th>
                    <th className="py-3 px-4">From Date</th>
                    <th className="py-3 px-4">To Date</th>
                    <th className="py-3 px-3 text-center">Days</th>
                    <th className="py-3 px-4">Reason</th>
                    <th className="py-3 px-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {leaves.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-semibold capitalize text-slate-900">{l.leave_type}</td>
                      <td className="py-3 px-4 font-mono text-slate-600">{new Date(l.from_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4 font-mono text-slate-600">{new Date(l.to_date).toLocaleDateString()}</td>
                      <td className="py-3 px-3 text-center font-bold text-slate-900">{l.days}</td>
                      <td className="py-3 px-4 text-slate-600">{l.reason || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${
                            l.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                            l.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-300' :
                            'bg-amber-100 text-amber-800 border border-amber-300'
                          }`}>
                            {l.status}
                          </span>
                          {l.status === 'pending' && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleUpdateLeaveStatus(l.id, 'approved')}
                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold shadow-xs transition"
                                title="Approve Leave Application"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleUpdateLeaveStatus(l.id, 'rejected')}
                                className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[10px] font-bold shadow-xs transition"
                                title="Reject Leave Application"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>

      {/* Apply Leave Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h3 className="font-bold text-slate-800 text-base">Submit Leave Application</h3>
              <button onClick={() => setShowLeaveModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            {leaveError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs">
                {leaveError}
              </div>
            )}

            <form onSubmit={handleApplyLeave} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Leave Type</label>
                <select
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs capitalize"
                >
                  <option value="casual">Casual Leave</option>
                  <option value="sick">Sick Leave</option>
                  <option value="annual">Annual Leave</option>
                  <option value="unpaid">Unpaid Leave</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Reason</label>
                <textarea
                  rows="3"
                  placeholder="Reason for leave request..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                ></textarea>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowLeaveModal(false)}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingLeave}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition disabled:opacity-50"
                >
                  {submittingLeave ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default StaffDetail;
